import React, { useState } from 'react';
import { Mail, Instagram, Send } from 'lucide-react';
import { brandInfo } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    artType: 'Sketch Art',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Your message has been sent! We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          artType: 'Sketch Art',
          message: ''
        });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="section-heading-custom mb-4">Get In Touch</h2>
            <p className="body-large-custom max-w-2xl mx-auto opacity-60">
              Ready to order custom artwork or have questions? Fill out the form below or reach out on Instagram
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-lg p-6 lg:p-8 shadow-sm">
              <h3 className="card-heading-custom mb-6">Send a Message</h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="label-custom">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-custom"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="label-custom">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-custom"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="artType" className="label-custom">
                    Art Type
                  </label>
                  <select
                    id="artType"
                    name="artType"
                    value={formData.artType}
                    onChange={handleChange}
                    className="input-custom"
                  >
                    <option value="Sketch Art">Sketch Art</option>
                    <option value="Watercolor Painting">Watercolor Painting</option>
                    <option value="Custom Portrait">Custom Portrait</option>
                    <option value="Other">Other / Custom Request</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="label-custom">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="input-custom resize-none"
                    placeholder="Tell us about your custom artwork idea..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="cta-button w-full"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message
                      <Send size={16} className="ml-2" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-lg p-6 lg:p-8 shadow-sm">
                <h3 className="card-heading-custom mb-6">Connect With Us</h3>
                
                <div className="space-y-6">
                  <a
                    href={brandInfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link-card"
                    style={{ background: '#ff84e4' }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="contact-icon">
                        <Instagram size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-black">Instagram</p>
                        <p className="body-small-custom opacity-60">@triansh_arts</p>
                      </div>
                    </div>
                  </a>

                  <a
                    href={brandInfo.sisterInstagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link-card"
                    style={{ background: '#ffe03d' }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="contact-icon">
                        <Instagram size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-black">More Paintings</p>
                        <p className="body-small-custom opacity-60">@sketchrally.co</p>
                      </div>
                    </div>
                  </a>

                  <div
                    className="contact-link-card"
                    style={{ background: '#88a2ff' }}
                  >
                    <a 
                      href={`mailto:${brandInfo.email}`}
                      className="flex items-center gap-4"
                    >
                      <div className="contact-icon">
                        <Mail size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-black">Email</p>
                        <p className="body-small-custom opacity-60">{brandInfo.email}</p>
                      </div>
                    </a>
                  </div>
                </div>
              </div>

              {/* Sister Company */}
              <div className="bg-white rounded-lg p-6 lg:p-8 shadow-sm">
                <h3 className="card-heading-custom mb-4">Explore More</h3>
                <p className="body-medium-custom opacity-80 mb-4">
                  Visit our sister company for an extensive collection of paintings and artwork.
                </p>
                <a
                  href={brandInfo.sisterWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button w-full text-center"
                >
                  Visit Sketch Rally Co
                </a>
              </div>

              {/* About Artist */}
              <div className="bg-white rounded-lg p-6 lg:p-8 shadow-sm">
                <h3 className="card-heading-custom mb-4">About the Artist</h3>
                <p className="body-medium-custom opacity-80 mb-4">
                  {brandInfo.about}
                </p>
                <p className="body-medium-custom">
                  All artwork is custom-made with prices starting below ₹799, ensuring quality art is accessible to everyone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
